import type { DashboardAppType } from '@pulsify/backend/dashboard-app'
import type { ErrorResponse } from '@pulsify/shared/errors'
import { AuthErrorCode } from '@pulsify/shared/errors'
import { NetworkError } from '@pulsify/shared/network-error'
import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from '@pulsify/shared/password'
import { hc } from 'hono/client'
import { adminBypassHeaders } from '~/app/admin-bypass'
import { env } from '~/env'
import * as m from '~/paraglide/messages'
import { getErrorMessage } from '~/shared/lib/api-errors'
import { activeProfileHeaders } from './active-profile-store'
import { getDeviceId } from './device-id'
import { supabase } from './supabase'

export { NetworkError }

export async function getAuthToken(): Promise<string | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  return session?.access_token ?? null
}

export const api = hc<DashboardAppType>(env.VITE_API_URL, {
  headers: async (): Promise<Record<string, string>> => {
    const token = await getAuthToken()
    const headers: Record<string, string> = {
      'X-Device-Id': getDeviceId(),
      ...adminBypassHeaders(),
      ...activeProfileHeaders(),
    }
    if (token) headers.Authorization = `Bearer ${token}`
    return headers
  },
})

interface ErrorDialogContent {
  title: string
  message: string
}

declare module '@pulsify/shared/network-error' {
  interface NetworkError {
    getDialogContent(): ErrorDialogContent
    /** Legacy alias for `statusCode` - old code used `err.status`. */
    readonly status: number | undefined
    details?: unknown
  }
}

/**
 * Render the error into a dialog-ready (title, message) pair via paraglide.
 * Mirrors the mobile app's `apps/app/services/core/network-error.ts`
 * augmentation in shape - same lookup order, same interpolation context -
 * but uses paraglide's compile-time-typed `m.foo()` functions instead of
 * i18next's runtime `t(key)`. Codes that don't have a paraglide mapping
 * yet fall back to the backend message.
 */
NetworkError.prototype.getDialogContent = function (): ErrorDialogContent {
  const title = m.error()

  if (!this.code) {
    return {
      title,
      message: this.message || m.unexpectedErrorOccurred(),
    }
  }

  // Password-policy codes need parameters and aren't covered by the flat
  // `errorMessageMap` in api/errors.ts, so handle them here.
  if (this.code === AuthErrorCode.PasswordTooShort) {
    return {
      title,
      message: m.passwordTooShortError({ minLength: PASSWORD_MIN_LENGTH }),
    }
  }
  if (this.code === AuthErrorCode.PasswordTooLong) {
    return {
      title,
      message: m.passwordTooLongError({ maxLength: PASSWORD_MAX_LENGTH }),
    }
  }

  // Delegate to the existing ~100-code lookup so we don't drift from the
  // `getErrorMessageFromError` path that legacy call sites still use.
  // Same precedence as before: map → backend message → generic.
  return { title, message: getErrorMessage(this.code, this.message) }
}

Object.defineProperty(NetworkError.prototype, 'status', {
  get(): number | undefined {
    return this.statusCode
  },
})

export async function unwrap<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body: ErrorResponse = await res.json().catch(() => ({
      code: 'InternalError',
      message: 'Request failed',
    }))
    const error = NetworkError.fromWire(res.status, body)
    error.details = body.details
    throw error
  }
  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return undefined as T
  }
  return res.json() as Promise<T>
}
